import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { BadgeCheck, Upload, FileText, AlertTriangle, Clock, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_LABEL } from "@/lib/categories";

const REQ_LABELS = {
  business_license: "Business License",
  insurance_certificate: "Certificate of Insurance",
  food_handler_permit: "Food Handler Permit",
  liquor_license: "Liquor License",
  health_inspection: "Health Inspection Certificate",
  professional_certification: "Professional Certifications",
  driver_license: "Commercial Driver's License (CDL)",
  vehicle_registration: "Vehicle Registration",
  portfolio_sample: "Portfolio / Work Sample",
  other: "Other Supporting Documents",
};

// Doc types offered in the manual upload dialog per category. Everything else
// (e.g. food_handler_permit for a DJ) is deliberately hidden. `other` is
// always available as a catch-all for extra supporting docs.
const ALLOWED_DOC_TYPES_BY_CATEGORY = {
  catering:       ["business_license", "insurance_certificate", "food_handler_permit", "liquor_license", "health_inspection", "other"],
  restaurant:     ["business_license", "insurance_certificate", "food_handler_permit", "liquor_license", "health_inspection", "other"],
  venue:          ["business_license", "insurance_certificate", "health_inspection", "liquor_license", "other"],
  dj:             ["business_license", "insurance_certificate", "professional_certification", "portfolio_sample", "other"],
  florist:        ["business_license", "insurance_certificate", "portfolio_sample", "other"],
  rentals:        ["business_license", "insurance_certificate", "other"],
  av:             ["business_license", "insurance_certificate", "professional_certification", "other"],
  security:       ["business_license", "insurance_certificate", "professional_certification", "other"],
  photography:    ["business_license", "insurance_certificate", "portfolio_sample", "professional_certification", "other"],
  transportation: ["business_license", "insurance_certificate", "driver_license", "vehicle_registration", "other"],
};

const DEFAULT_ALLOWED = ["business_license", "insurance_certificate", "other"];

const STATUS_STYLES = {
  pending: "bg-[#EFEADF] text-[#5C5C5C]",
  approved: "bg-[color:var(--verified-soft)] text-[color:var(--verified)]",
  rejected: "bg-[#FBEAE4] text-[color:var(--accent)]",
};

export default function VendorVerification({ vendors }) {
  const [selectedVendorId, setSelectedVendorId] = useState(vendors?.[0]?.vendor_id || null);
  const [statusMap, setStatusMap] = useState({}); // vendor_id -> status obj
  const [uploadOpen, setUploadOpen] = useState(false);
  const [preselectType, setPreselectType] = useState("business_license");

  useEffect(() => {
    if (!selectedVendorId && vendors?.length) setSelectedVendorId(vendors[0].vendor_id);
  }, [vendors, selectedVendorId]);

  const loadStatus = async (vendorId) => {
    if (!vendorId) return;
    try {
      const { data } = await api.get(`/verification/status/${vendorId}`);
      setStatusMap((m) => ({ ...m, [vendorId]: data }));
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (selectedVendorId) loadStatus(selectedVendorId);
  }, [selectedVendorId]);

  const status = statusMap[selectedVendorId];
  const selected = vendors?.find((v) => v.vendor_id === selectedVendorId);

  const openUpload = (docType) => { setPreselectType(docType); setUploadOpen(true); };

  const onUploaded = () => {
    setUploadOpen(false);
    loadStatus(selectedVendorId);
    toast.success("Document submitted for review");
  };

  const removeDoc = async (docId) => {
    try {
      await api.delete(`/verification/documents/${docId}`);
      toast.success("Document removed");
      loadStatus(selectedVendorId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!vendors?.length) {
    return (
      <div data-testid="verification-empty" className="rounded-2xl border border-[color:var(--border)] bg-white p-8">
        <div className="eyebrow mb-2">Verification</div>
        <div className="text-lg">Create a listing first to unlock the Verification Center.</div>
      </div>
    );
  }

  return (
    <div data-testid="vendor-verification" className="space-y-6">
      {/* Vendor picker + status summary */}
      <div className="rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <div className="eyebrow mb-2 flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" /> Verification Center
            </div>
            <h2 className="text-2xl sm:text-3xl font-semibold" data-testid="verification-heading">
              Build trust with the NContro badge
            </h2>
            <p className="text-sm text-[color:var(--text-muted)] mt-2 max-w-xl leading-relaxed">
              Upload your business documents so we can display the <strong className="text-[color:var(--text)]">Verified by NContro</strong> badge on your public profile and in search results. Verification is optional but strongly encouraged — verified vendors convert 3× better.
            </p>
          </div>
          {vendors.length > 1 && (
            <Select value={selectedVendorId} onValueChange={setSelectedVendorId}>
              <SelectTrigger data-testid="vendor-picker" className="bg-white h-11 w-full sm:w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((v) => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {status && (
          <div className="mt-6 border-t border-[color:var(--border)] pt-6">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm">
                <span className="font-semibold">{selected?.name}</span>
                <span className="text-[color:var(--text-muted)]"> · {CATEGORY_LABEL[selected?.category] || selected?.category}</span>
              </div>
              {status.verified ? (
                <span data-testid="verified-badge" className="badge-verified">
                  <BadgeCheck className="w-3.5 h-3.5" /> Verified by NContro
                </span>
              ) : (
                <span className="text-xs font-semibold text-[color:var(--text-muted)]">
                  {status.progress_percent}% complete
                </span>
              )}
            </div>
            <div className="w-full h-2 rounded-full bg-[color:var(--surface)] overflow-hidden">
              <div
                data-testid="verification-progress"
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${status.progress_percent}%`,
                  background: status.verified ? "var(--verified)" : "var(--accent)",
                }}
              />
            </div>
            {status.expiring_soon_types?.length > 0 && (
              <div className="mt-4 flex items-start gap-2 text-xs text-[color:var(--accent)]">
                <Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Expiring within 30 days: {status.expiring_soon_types.map((t) => REQ_LABELS[t] || t).join(", ")}. Upload a replacement to keep your badge active — we'll keep your history.
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Required documents checklist */}
      {status && (
        <div className="rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8">
          <div className="eyebrow mb-4">Required documents for {CATEGORY_LABEL[selected?.category] || selected?.category}</div>
          <div className="space-y-3">
            {status.required.map((type) => {
              const doc = status.documents.find((d) => d.doc_type === type && d.status !== "rejected");
              const isApproved = doc?.status === "approved";
              return (
                <div key={type} data-testid={`req-row-${type}`} className="flex items-center justify-between gap-3 p-4 rounded-xl border border-[color:var(--border)]">
                  <div className="flex items-center gap-3 min-w-0">
                    {isApproved ? (
                      <BadgeCheck className="w-5 h-5 text-[color:var(--verified)] shrink-0" />
                    ) : (
                      <FileText className="w-5 h-5 text-[color:var(--text-muted)] shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{REQ_LABELS[type] || type}</div>
                      {doc ? (
                        <div className="text-xs text-[color:var(--text-muted)] mt-0.5">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${STATUS_STYLES[doc.status]}`}>{doc.status}</span>
                          <span className="ml-2 truncate">{doc.file_name}</span>
                        </div>
                      ) : (
                        <div className="text-xs text-[color:var(--text-muted)] mt-0.5">Not yet uploaded</div>
                      )}
                    </div>
                  </div>
                  <Button
                    data-testid={`upload-${type}-btn`}
                    onClick={() => openUpload(type)}
                    variant="outline"
                    className="rounded-lg h-9 px-4 border-[color:var(--border-strong)]"
                  >
                    <Upload className="w-3.5 h-3.5 mr-2" /> {doc ? "Replace" : "Upload"}
                  </Button>
                </div>
              );
            })}
          </div>
          <div className="mt-6 pt-6 border-t border-[color:var(--border)] flex items-center justify-between">
            <div className="text-sm text-[color:var(--text-muted)]">
              Have another supporting document? Add optional certifications, awards, or specialty permits.
            </div>
            <Button data-testid="upload-other-btn" onClick={() => openUpload("other")} className="btn-sage rounded-lg h-10">
              <Upload className="w-4 h-4 mr-2" /> Add document
            </Button>
          </div>
        </div>
      )}

      {/* Historical list of ALL docs (including rejected + others) */}
      {status?.documents?.length > 0 && (
        <div className="rounded-2xl border border-[color:var(--border)] bg-white p-6 sm:p-8">
          <div className="eyebrow mb-4">Upload history</div>
          <div className="space-y-3" data-testid="upload-history">
            {status.documents.map((d) => (
              <div key={d.document_id} className="flex items-center justify-between gap-3 p-4 rounded-xl border border-[color:var(--border)]">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{REQ_LABELS[d.doc_type] || d.doc_type_label}</span>
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${STATUS_STYLES[d.status]}`}>{d.status}</span>
                  </div>
                  <div className="text-xs text-[color:var(--text-muted)] mt-1 truncate">
                    {d.file_name} · uploaded {new Date(d.uploaded_at).toLocaleDateString()}
                    {d.expires_at && ` · expires ${new Date(d.expires_at).toLocaleDateString()}`}
                  </div>
                  {d.reviewer_notes && (
                    <div className="mt-2 text-xs italic text-[color:var(--text-muted)] leading-relaxed">
                      Reviewer: "{d.reviewer_notes}"
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeDoc(d.document_id)}
                  data-testid={`delete-doc-${d.document_id}`}
                  aria-label="Delete"
                  className="w-9 h-9 rounded-lg border border-[color:var(--border)] flex items-center justify-center hover:bg-[color:var(--surface)]"
                >
                  <Trash2 className="w-4 h-4 text-[color:var(--text-muted)]" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        vendorId={selectedVendorId}
        vendorCategory={selected?.category}
        initialType={preselectType}
        onSuccess={onUploaded}
      />
    </div>
  );
}

function UploadDialog({ open, onClose, vendorId, vendorCategory, initialType, onSuccess }) {
  const allowedTypes = ALLOWED_DOC_TYPES_BY_CATEGORY[vendorCategory] || DEFAULT_ALLOWED;
  // If initialType is not in the allowed list (e.g. a required doc from a
  // legacy category), still include it so the vendor can complete the upload
  // they came here for.
  const availableTypes = allowedTypes.includes(initialType)
    ? allowedTypes
    : [initialType, ...allowedTypes];
  const [docType, setDocType] = useState(initialType);
  const [file, setFile] = useState(null);
  const [expiresAt, setExpiresAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  useEffect(() => { setDocType(initialType); setFile(null); setExpiresAt(""); }, [initialType, open]);

  const submit = async () => {
    if (!file) { toast.error("Please choose a file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File exceeds 5MB limit"); return; }
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) {
      toast.error("Only PDF, JPG, JPEG, or PNG files are allowed");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = reader.result.split(",")[1] || "";
        try {
          await api.post("/verification/documents", {
            vendor_id: vendorId,
            doc_type: docType,
            file_name: file.name,
            file_type: file.type,
            file_data_b64: b64,
            expires_at: expiresAt || null,
          });
          onSuccess();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail));
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      setUploading(false);
      toast.error("Could not read file");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="upload-dialog" className="max-w-lg bg-[color:var(--bg)]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold">Upload document</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <div className="eyebrow mb-2">Document type</div>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="upload-doctype" className="bg-white h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {availableTypes.map((k) => (
                  <SelectItem key={k} value={k}>{REQ_LABELS[k] || k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="eyebrow mb-2">File (PDF, JPG, PNG · max 5MB)</div>
            <input
              ref={inputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="upload-file"
              className="block w-full text-sm text-[color:var(--text-muted)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[color:var(--surface)] file:text-[color:var(--text)] hover:file:bg-[color:var(--surface-2)]"
            />
            {file && <div className="text-xs text-[color:var(--text-muted)] mt-2">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
          </div>
          <div>
            <div className="eyebrow mb-2">Expiration date (if applicable)</div>
            <Input data-testid="upload-expires" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} className="bg-white h-11" />
          </div>
          <div className="p-3 rounded-lg bg-[color:var(--surface)] text-xs text-[color:var(--text-muted)] flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[color:var(--accent)] mt-0.5 shrink-0" />
            <span>Documents are encrypted at rest and shared only with the NContro review team. We use them solely to verify your business.</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button data-testid="upload-submit" onClick={submit} disabled={uploading || !file} className="btn-sage rounded-lg">
            {uploading ? "Uploading…" : "Submit for review"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
